import { type ReactNode, useEffect, useRef } from "react";
import { tabWithin } from "./Dialog.tsx";
import { useKeptScroll } from "./kept.ts";
import { Icon } from "./souvenir.ts";

// On a computer, what's opened to be read in full as a modal over the trip: a finding (features/research.md)
// and an option's card (features/options.md, "Tiles of one height"). A raised panel centred over a scrim
// that dims the whole page, the trip's bar too, scrolling inside itself, its X at its top right. A click on
// the scrim closes what's on top (`onScrim`: a card opened from it, else itself). What's opened from it
// (`card`, what a finding is about) lies above it at the right, and the trip's notices (`notices`) above the
// scrim, so a new version's Refresh is never under it. It's one dialog: the page under it is inert
// (TripPage) and Tab goes round what's in it. Back finds where it was scrolled (`name`).
export function Modal({
  name,
  label,
  className,
  onClose,
  onScrim,
  card = null,
  notices,
  inert = false,
  children,
}: {
  name: string;
  label: string;
  // What it holds, for its own look (`is-card`: an option's card, its head beside the X).
  className?: string;
  onClose: () => void;
  onScrim: () => void;
  card?: ReactNode;
  notices: ReactNode;
  // Under another modal (a finding opened from the card in it): out of reach, as the page is.
  inert?: boolean;
  children: ReactNode;
}) {
  const root = useRef<HTMLDivElement>(null);
  const body = useRef<HTMLDivElement>(null);
  useKeptScroll(body, name);
  useEffect(() => {
    if (inert) return;
    const onKey = (e: KeyboardEvent) => {
      // A dialog asking first (Dialog.tsx), over what's in it, keeps its own Tab.
      if (root.current && !document.querySelector("dialog[open]")) tabWithin(root.current, e);
    };
    addEventListener("keydown", onKey, true);
    return () => removeEventListener("keydown", onKey, true);
  }, [inert]);
  return (
    <div
      className={`tm-modal${className ? ` ${className}` : ""}`}
      role="dialog"
      aria-modal="true"
      aria-label={label}
      ref={root}
      inert={inert}
    >
      <div className="tm-modal-scrim" aria-hidden="true" onClick={onScrim} />
      <div className="tm-modal-panel">
        <button type="button" className="tm-layer-close tm-modal-close" aria-label="Close" onClick={onClose}>
          <Icon name="close" size={20} />
        </button>
        <div className="tm-modal-body" ref={body}>
          {children}
        </div>
      </div>
      {card}
      {notices}
    </div>
  );
}
